<template>
    <div class="grid-content">
        <div v-for="(prop) in parsedFields"
             class="grid-item grid-item-half grid-item-break-half"
             :key="prop.name">
            <r-input
                v-if="isTextInput(prop.type)"
                :label="prop.label"
                :password="isPasswordField(prop)"
                :name="prop.name"
                :validate="$v.fieldConfig[prop.name]"
                :disabled="isFieldDisabled(prop)"
                :placeholder="getPlaceholder(prop)"
                v-model="fieldConfig[prop.name]"
                :ref="prop.name">
                <r-button
                    slot="right-button"
                    class="no-flex"
                    @click="enableUpdate(prop)"
                    v-if="isUpdatingPasswordField(prop)">Edit
                </r-button>
            </r-input>
            <div v-if="isMultiselect(prop.type)">
                <v-select
                    :label="prop.label"
                    class="multiselect--allow-empty"
                    :placeholder="prop.placeholder"
                    :options="prop.items"
                    :multiple="allowsMultipleSelection(prop)"
                    :allow-empty="allowsMultipleSelection(prop)"
                    v-model="fieldConfig[prop.name]"
                />
            </div>
            <r-checkbox
                v-if="prop.type === fieldTypes.boolean"
                :label="prop.label"
                :value="true"
                caption="Toggle feature on or off"
                v-model="fieldConfig[prop.name]"/>
        </div>
        <div v-if="$v.fieldConfig.$error" class="grid-item grid-item-full stack-m">
            <v-error-messages>
                <li v-for="(field) in fieldErrors" class="list-item" v-html="getFieldErrorMessage(field)"></li>
            </v-error-messages>
        </div>
    </div>
</template>
<script>
    import {
        vErrorMessages, vSelect,
    } from '@/components/form';
    import {required, numeric} from 'vuelidate/lib/validators';
    import _ from '@/common/helpers';
    import {CURRENCIES_LIST} from '@/data/currencies';
    import {schema} from '@/common/spec/spec-cache';
    import {getFieldOverrides} from '@/modules/gateway-accounts/special-config';

    const FieldTypes = {
        string: 'string',
        boolean: 'boolean',
        integer: 'integer',
        array: 'array',
    };

    /**
     * Generate field overrides and inject dependencies
     */
    const fieldOverrides = getFieldOverrides({fieldTypes: FieldTypes, schema});

    export default {
        name: 'dynamic-field-configuration',
        components: {
            vErrorMessages,
            vSelect,
        },
        props: {
            fields: {
                type: Object,
            },
            config: {
                type: Object,
            },
            isUpdating: {
                type: Boolean,
                default: false,
            },
            gatewayName: {
                type: String,
            },
        },
        computed: {
            parsedFields() {
                if (this.fields) {
                    const properties = this.fields.properties || {};
                    const requiredFields = this.fields.required || [];
                    return Object.keys(properties).map((propertyName) => {
                        const baseField = {
                            name: propertyName,
                            label: this.prepareLabel(propertyName),
                            type: properties[propertyName].type,
                            format: properties[propertyName].format || null,
                            required: !this.isUpdating && requiredFields.includes(propertyName),
                        };
                        if (propertyName in fieldOverrides) {
                            return {
                                ...baseField,
                                ...fieldOverrides[propertyName]({gatewayName: this.gatewayName}),
                            };
                        }
                        return baseField;
                    });
                }
                return [];
            },
            requiredFields() {
                return this.parsedFields
                    .filter(field => field.required && field.type !== FieldTypes.boolean);
            },
            fieldErrors() {
                if (this.$v.fieldConfig.$error) {
                    return this.requiredFields
                        .filter(({name}) => this.isFieldRequiredError(name)
                            || this.isFieldNumericError(name));
                }
                return [];
            },
        },
        watch: {
            config(data) {
                // whenever the config is changed in the parent component
                // we update the local copy
                this.fieldConfig = {...data};
            },
        },
        data() {
            return {
                fieldTypes: FieldTypes,
                // if the config is present (on update), use it,
                // otherwise use a plain object to allow for property injection
                fieldConfig: this.config || {},
                isUpdatingOptionalField: !this.isUpdating,
                currenciesList: [...CURRENCIES_LIST],
            };
        },
        validations() {
            // generate validation from gateway config fields
            return {
                fieldConfig:
                    this.requiredFields
                        .reduce((settings, field) => {
                            settings[field.name] = {required};
                            if (field.type === FieldTypes.integer) {
                                settings[field.name].numeric = numeric;
                            }
                            return settings;
                        }, {}),
            };
        },
        methods: {
            checkDefaultBoolean() {
                if (!this.config && typeof this.fieldConfig === 'object') {
                    const {properties} = this.fields;
                    Object.keys(properties).forEach((key) => {
                        if (properties[key].type === FieldTypes.boolean) {
                            this.fieldConfig = {
                                ...this.fieldConfig,
                                [key]: properties[key].default,
                            };
                        }
                    });
                }
                return this.config;
            },
            prepareLabel(propertyName) {
                let label = propertyName;
                if (propertyName.includes('_')) {
                    label = label.replace(/_/g, ' ');
                }
                if (propertyName.includes('-')) {
                    label = _.unKebabCase(label);
                }
                return _.unCamelCase(label);
            },
            isTextInput(type) {
                return [FieldTypes.integer, FieldTypes.string].includes(type);
            },
            isMultiselect(type) {
                return type === FieldTypes.array;
            },
            allowsMultipleSelection(field) {
                if (field.options && field.options.multiple === true) {
                    return true;
                }
                return false;
            },
            isPasswordField({format}) {
                return format === 'password';
            },
            /**
             * Checks whether the field is of password type and in a form that is
             * currently updating. In this case we render an extra `edit` button on the
             * right of the field.
             * @param {{}} field
             * @returns {Boolean}
             */
            isUpdatingPasswordField(field) {
                return this.isUpdating && this.isPasswordField(field);
            },
            isFieldDisabled(field) {
                return this.isUpdatingPasswordField(field) && !this.isUpdatingOptionalField;
            },
            getPlaceholder(field) {
                if (this.isUpdatingPasswordField(field)) {
                    return '********';
                }
                return null;
            },
            enableUpdate({name}) {
                this.isUpdatingOptionalField = true;
                this.$nextTick(() => this.$refs[name][0].getFocus());
            },
            getConfig() {
                return {...this.fieldConfig};
            },
            validate() {
                this.$v.$touch();
                // trigger data update on validation
                // to prevent parent/child infinite data update loops
                this.triggerUpdate();
                return this.$v.fieldConfig.$error;
            },
            triggerUpdate() {
                if (!this.$v.fieldConfig.$error) {
                    this.$emit('input', this.getConfig());
                }
            },
            isFieldRequiredError(name) {
                return this.$v.fieldConfig[name].required === false;
            },
            isFieldNumericError(name) {
                return this.$v.fieldConfig[name].numeric === false;
            },
            getFieldErrorMessage({name, label}) {
                if (this.isFieldRequiredError(name)) {
                    return `${label} is required.`;
                } if (this.isFieldNumericError(name)) {
                    return `${label} must be numerical.`;
                }
                return null;
            },
        },
        mounted() {
            this.checkDefaultBoolean();
        },
    };
</script>
